import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomStringParam from '../helpers/custom-string-param';
import transformValueForUrl from '../helpers/transform-to-url';

// types
import {
  SortDir,
  Sort,
  Filters,
  SchemaFilterType,
  SchemaProps,
} from '../types';

export interface UseQueryState {
  filters: Filters;
  sort: Sort;
  onSortChange: (name: string) => void;
  onFilterChange: (values: Filters) => void;
}

export default function useQueryState(
  schema: SchemaProps,
  withoutChangeSearchParams?: boolean,
): UseQueryState {
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState<Sort>({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [schemaWithPagination, setSchemaWithPagination] = useState<
    SchemaProps | undefined
  >(undefined);

  useEffect(() => {
    if (!schemaWithPagination) {
      let nextSearchParams: Record<string, string | string[]> = {};

      searchParams.forEach((value, key) => {
        if (nextSearchParams[key]) {
          const prevValue = nextSearchParams[key];

          const nextSearchParamsArray = ([] as string[]).concat(prevValue);

          nextSearchParamsArray.push(value);

          nextSearchParams[key] = nextSearchParamsArray;
        } else {
          nextSearchParams[key] = value;
        }
      });

      const nextSchemaWithPagination: SchemaProps = {
        ...schema,
        cursor: {
          value: CustomStringParam,
          type: SchemaFilterType.STRING,
        },
      };

      setSchemaWithPagination(nextSchemaWithPagination);

      if (
        nextSchemaWithPagination?.sortProp?.value.default &&
        nextSchemaWithPagination?.sortDir.value.default
      ) {
        nextSearchParams = {
          ...nextSearchParams,
          sortProp:
            nextSearchParams.sortProp ||
            nextSchemaWithPagination?.sortProp?.value.default,
          sortDir:
            nextSearchParams.sortDir ||
            nextSchemaWithPagination?.sortDir.value.default,
        };

        if (!withoutChangeSearchParams) {
          setSearchParams(nextSearchParams);
        }
      }
    }
  }, [schema, schemaWithPagination, setSearchParams, searchParams]);

  useEffect(() => {
    // transform URL state to query state
    const currentFiltersParams: Record<
      string,
      {
        value: any;
        type: SchemaFilterType;
      }
    > = {};

    const currentSortParams = {
      sortDir: schema?.sortDir?.value?.default || '',
      sortProp: schema?.sortProp?.value?.default || '',
    };

    const keys: {
      key: string;
      value: string;
    }[] = [];

    if (schemaWithPagination) {
      searchParams.forEach((value, key) => {
        if (key !== 'sortDir' && key !== 'sortProp') {
          keys.push({
            key,
            value,
          });

          const currentSchema = schemaWithPagination[key];

          if (currentSchema) {
            if (currentSchema?.type === SchemaFilterType.DATE_RANGE) {
              let rangeSchema: {
                queryNames?: string[];
                name?: string;
                withTimeSelector?: boolean;
              } = {};

              Object.keys(schemaWithPagination).forEach((item) => {
                if (schemaWithPagination[item].queryNames) {
                  if (schemaWithPagination[item]?.queryNames?.includes(key)) {
                    rangeSchema = {
                      queryNames: schemaWithPagination[item]?.queryNames,
                      name: item,
                      withTimeSelector:
                        schemaWithPagination[item]?.withTimeSelector,
                    };
                  }
                }
              });

              if (
                rangeSchema?.queryNames &&
                key === rangeSchema?.queryNames[0]
              ) {
                currentFiltersParams[rangeSchema?.name as string] = {
                  value: [value],
                  type: SchemaFilterType.DATE_RANGE,
                };
              } else if (keys.filter((item) => item.key === key)) {
                currentFiltersParams[rangeSchema?.name as string] = {
                  value: [
                    ...currentFiltersParams[rangeSchema?.name as string].value,
                    value,
                  ],
                  type: SchemaFilterType.DATE_RANGE,
                };
              }
            } else if (keys.filter((item) => item.key === key)) {
              const values = keys.filter((item) => item.key === key);
              const currentType = schemaWithPagination[key]?.type;

              currentFiltersParams[key] = {
                value: values.map((item) => item.value),
                type: currentType || SchemaFilterType.STRING,
              };
            }
          }
        } else if (key === 'sortDir' || key === 'sortProp') {
          if (
            key === 'sortProp' &&
            schemaWithPagination?.sortProp?.value.default &&
            value !== 'null'
          ) {
            currentSortParams[key] =
              schemaWithPagination[key].value.decode(value);
          }

          if (
            key === 'sortDir' &&
            schemaWithPagination?.sortDir?.value.default &&
            value !== 'null'
          ) {
            currentSortParams[key] =
              schemaWithPagination[key].value.decode(value);
          }
        }
      });

      setFilters(currentFiltersParams);
      setSort(currentSortParams);
    }
  }, [searchParams, schemaWithPagination]);

  const onSortChange = (
    columnName: string,
    direction?: string,
    queryFilters?: Filters,
  ) => {
    const sortProp = searchParams.get('sortProp');
    let sortDir = searchParams.get('sortDir');

    if (direction) {
      sortDir = direction;
    }

    let nextState = {
      sortProp,
      sortDir,
    };

    if (sortProp === columnName) {
      if (direction) {
        nextState.sortDir = direction;
      } else {
        nextState.sortDir =
          sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC;
      }
    } else {
      nextState = {
        sortProp: columnName,
        // eslint-disable-next-line no-nested-ternary
        sortDir: withoutChangeSearchParams
          ? (direction as SortDir) === SortDir.ASC
            ? SortDir.DESC
            : SortDir.ASC
          : SortDir.ASC,
      };
    }

    let nextFilters = { ...filters };

    if (queryFilters) {
      nextFilters = {
        ...nextFilters,
        ...queryFilters,
      };
    }

    const prevSearchParams = transformValueForUrl(
      nextFilters,
      schemaWithPagination as SchemaProps,
    );

    const nextSearchParams: any = {
      ...prevSearchParams,
      ...nextState,
    };

    if (!withoutChangeSearchParams) {
      setSearchParams(nextSearchParams);
    } else {
      setFilters(nextFilters);
      setSort(nextState as Sort);
    }
  };

  const onFilterChange = (filterValues: Filters) => {
    const sortProp = searchParams.get('sortProp');
    const sortDir = searchParams.get('sortDir');

    const nextFilterValues = {
      ...filterValues,
      sortDir: {
        value:
          sortDir === 'null' || sortDir === null
            ? filterValues?.sortDir?.value
            : [sortDir],
        type: SchemaFilterType.STRING,
      },
      sortProp: {
        value:
          sortProp === 'null' || sortProp === null
            ? filterValues?.sortProp?.value
            : [sortProp],
        type: SchemaFilterType.STRING,
      },
    };

    const nextSearchParams = transformValueForUrl(
      nextFilterValues,
      schemaWithPagination as SchemaProps,
    );

    if (!withoutChangeSearchParams) {
      setSearchParams(nextSearchParams);
    } else {
      setFilters(nextFilterValues);
    }
  };

  return {
    filters,
    sort,
    onSortChange,
    onFilterChange,
  };
}
